import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHeart, FiUsers, FiTarget, FiTrendingUp, FiShield, FiGlobe } = FiIcons;

const About = () => {
  const values = [
    {
      icon: FiHeart,
      title: 'Inclusive by Design',
      description: 'Every platform, channel, and community we build prioritizes authentic LGBTQ+ representation and safe spaces for creators to thrive.'
    },
    {
      icon: FiUsers,
      title: 'Community-First Approach',
      description: 'We help creators build meaningful connections with their audiences through community-driven platforms and engagement strategies.'
    },
    {
      icon: FiShield,
      title: 'Creator Ownership',
      description: 'Break free from algorithm dependency. We empower creators to own their content, audience, and revenue streams across multiple platforms.'
    }
  ];

  const achievements = [
    {
      icon: FiTarget,
      title: 'Strategic Focus',
      description: 'Specialized expertise in LGBTQ+ content creation, niche community building, and NYC market dynamics.'
    },
    {
      icon: FiTrendingUp,
      title: 'Proven Results',
      description: 'Our clients see average growth of 300% in audience engagement and 250% increase in monetization within 6 months.'
    },
    {
      icon: FiGlobe,
      title: 'Multi-Platform Mastery',
      description: 'From OTT streaming channels to social media management, we create cohesive digital ecosystems for sustainable growth.'
    }
  ];

  return (
    <section id="about" className="py-20 lg:py-32 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Redefining Media
            </span>
            <br />
            <span className="text-white">for LGBTQ+ Creators</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Create Space Media was founded on the belief that LGBTQ+ content creators and niche communities 
            deserve more than just social media algorithms. Based in the heart of New York City, we're a 
            full-service media agency dedicated to helping creators build sustainable, community-driven 
            digital ecosystems that prioritize authentic representation and creator ownership.
          </p>
        </motion.div>

        {/* Mission Statement */}
        <motion.div
          className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 backdrop-blur-sm border border-purple-500/20 rounded-3xl p-8 lg:p-12 mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="text-center">
            <SafeIcon icon={FiHeart} className="text-5xl text-pink-400 mx-auto mb-6" />
            <h3 className="text-2xl lg:text-3xl font-bold mb-6 text-white">Our Mission</h3>
            <p className="text-lg text-gray-300 max-w-4xl mx-auto leading-relaxed">
              We empower LGBTQ+ content creators, queer-owned businesses, and underserved niche communities 
              in New York City and beyond to build meaningful, monetizable digital presence across multiple 
              platforms. Our approach goes beyond traditional social media management – we create comprehensive 
              digital ecosystems including OTT streaming channels, community-based platforms, and custom 
              audience engagement tools that give creators true ownership of their content and community.
            </p>
          </div>
        </motion.div>

        {/* Values Grid */}
        <div className="mb-20">
          <motion.h3
            className="text-3xl lg:text-4xl font-bold text-center mb-12 text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            What Sets Us Apart
          </motion.h3>
          
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-8 hover:border-purple-500/50 transition-all duration-300 group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
              >
                <SafeIcon icon={value.icon} className="text-4xl text-purple-400 mb-6 group-hover:text-pink-400 transition-colors duration-300" />
                <h4 className="text-xl font-bold text-white mb-4">{value.title}</h4>
                <p className="text-gray-300 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Achievements Grid */}
        <div>
          <motion.h3
            className="text-3xl lg:text-4xl font-bold text-center mb-12 text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Why NYC Creators Choose Us
          </motion.h3>
          
          <div className="grid md:grid-cols-3 gap-8">
            {achievements.map((achievement, index) => (
              <motion.div
                key={achievement.title}
                className="text-center group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-gray-700 group-hover:border-purple-500/50 rounded-2xl p-8 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/10">
                  <SafeIcon icon={achievement.icon} className="text-5xl text-purple-400 mx-auto mb-6 group-hover:text-pink-400 transition-colors duration-300" />
                  <h4 className="text-xl font-bold text-white mb-4">{achievement.title}</h4>
                  <p className="text-gray-300 leading-relaxed">{achievement.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* NYC Connection */}
        <motion.div
          className="mt-20 text-center bg-gradient-to-r from-gray-800/30 to-gray-900/30 backdrop-blur-sm border border-gray-700 rounded-3xl p-8 lg:p-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h3 className="text-2xl lg:text-3xl font-bold mb-6 text-white">Proudly NYC-Based</h3>
          <p className="text-lg text-gray-300 max-w-4xl mx-auto leading-relaxed">
            New York City isn't just our home – it's the creative heartbeat that drives everything we do. 
            We understand the unique challenges and opportunities facing LGBTQ+ content creators in the NYC 
            market, from navigating local community dynamics to leveraging the city's incredible diversity 
            and creative energy. Whether you're a Brooklyn-based YouTuber, a Manhattan drag performer 
            building an OTT channel, or a Queens-based activist creating community platforms, we speak 
            your language and understand your world.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default About;